import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useStore from "hooks/useStore";
import { Auth_APIS } from "libs/apis/auth.api";
import {
  errorToaster,
  successToaster,
} from "utils/helpers/common/alert-service";
import bgFixed from "assets/images/others/host-side-images/bgFixed.png";
import { siteRoutes } from "utils/helpers/enums/routes";

const HostSubscriptionMobile = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setToken, setRegisteredData } = useStore();

  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<
    "idle" | "verifying" | "success" | "error" | "missing-token"
  >("idle");

  // Prevent multiple verification attempts
  const hasVerifiedRef = useRef(false);

  const token = searchParams.get("token");

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        if (!hasVerifiedRef.current) {
          navigate(siteRoutes.home);
        }
        return;
      }

      if (
        hasVerifiedRef.current ||
        isVerifying ||
        verificationStatus !== "idle"
      ) {
        return;
      }

      hasVerifiedRef.current = true;
      setIsVerifying(true);
      setVerificationStatus("verifying");

      const tokenToSave = token;

      try {
        // Temporarily set the token for verification
        localStorage.setItem("token", tokenToSave);
        setToken(tokenToSave);

        // Verify by fetching user profile
        const response = await Auth_APIS.getUserProfile();

        if (response?.error)
          throw new Error(response.error || "Token verification failed");

        const { data = null, status = false } = response || {};

        if (status && data) {
          const finalToken = data.jwtToken || tokenToSave;

          // Save token to localStorage + Redux
          localStorage.setItem("token", finalToken);
          setToken(finalToken);

          const dataToSave = { ...data };
          if (dataToSave.jwtToken) delete dataToSave.jwtToken;

          setRegisteredData(dataToSave);
          setVerificationStatus("success");
          successToaster("Successfully authenticated from mobile app");

          // Remove token from URL for security
          searchParams.delete("token");
          setSearchParams(searchParams, { replace: true });

          // Confirm token persisted; stay on this page
          setTimeout(() => {
            const savedToken = localStorage.getItem("token");
            if (!savedToken) {
              console.warn("Token missing after verification persist check.");
            }
            setVerificationStatus("success");
          }, 100);
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error: any) {
        console.error("❌ Token verification failed:", error?.message || error);

        setVerificationStatus("error");

        try {
          localStorage.removeItem("token");
          setToken(null);
        } catch (e) {
          console.error("Error clearing token:", e);
        }

        const errorMessage =
          error?.response?.status === 401 || error?.status === 401
            ? "Authentication token is invalid or expired"
            : error?.message ||
              "Failed to verify authentication token. Please try again.";

        errorToaster(errorMessage);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ⏳ Loading
  if (isVerifying || verificationStatus === "verifying") {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div
          style={{ backgroundImage: `url(${bgFixed})` }}
          className="bg-cover bg-center bg-no-repeat h-full w-full relative"
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center max-w-md backdrop-blur-sm rounded-2xl p-10 border border-black/20">
              <div className="flex flex-col items-center space-y-6">
                {/* Animated Loading Spinner */}
                <div className="relative">
                  <div className="w-20 h-20 border-4 border-gray-300 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-20 h-20 border-4 border-white rounded-full border-t-transparent animate-spin"></div>
                </div>
                
                <div className="text-center space-y-3">
                  <h2 className="text-3xl font-bold text-white">
                    Verifying Authentication
                  </h2>
                  <p className="text-gray-200 text-lg leading-relaxed">
                    Please wait while we securely verify your session...
                  </p>
                </div>

                {/* Animated Progress Dots */}
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 🚫 Missing Token
  if (verificationStatus === "missing-token") {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div
          style={{ backgroundImage: `url(${bgFixed})` }}
          className="bg-cover bg-center bg-no-repeat h-full w-full relative"
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center max-w-md backdrop-blur-sm rounded-2xl p-10 border border-black/20">
              <div className="flex flex-col items-center space-y-6">
                {/* Error Icon */}
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                
                <div className="text-center space-y-3">
                  <h2 className="text-3xl font-bold text-white">
                    Authentication Token Missing
                  </h2>
                  <p className="text-gray-200 text-lg leading-relaxed">
                    This link must be opened from the mobile app to sign you in securely.
                  </p>
                  <p className="text-gray-300">
                    Please return to the app and try again.
                  </p>
                </div>

                
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ❌ Error
  if (verificationStatus === "error") {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div
          style={{ backgroundImage: `url(${bgFixed})` }}
          className="bg-cover bg-center bg-no-repeat h-full w-full relative"
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center max-w-md backdrop-blur-sm rounded-2xl p-10 border border-black/20">
              <div className="flex flex-col items-center space-y-6">
                {/* Error Icon */}
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center animate-pulse">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                
                <div className="text-center space-y-3">
                  <h2 className="text-3xl font-bold text-white">
                    Authentication Failed
                  </h2>
                  <p className="text-gray-200 text-lg">
                    We'll redirect you to the login page...
                  </p>
                </div>

                {/* Redirect Countdown */}
                <div className="flex items-center space-x-2 text-gray-300">
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Redirecting...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Default/Success Section
  return (
    <div className="h-screen w-full flex items-center justify-center">
      <div
        style={{ backgroundImage: `url(${bgFixed})` }}
        className="bg-cover bg-center bg-no-repeat h-full w-full relative"
      >
        {/* Overlay with blur + transparent background */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center max-w-2xl backdrop-blur-sm rounded-2xl p-10 border border-black/20">
        
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Discover Amazing Events
            </h1>
            <p className="text-lg md:text-2xl text-gray-200 mb-8">
              Join our community and start enjoying unforgettable experiences
              today
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate(siteRoutes.subscription)}
                className="px-8 py-3 bg-white text-black rounded-full text-lg font-semibold hover:bg-gray-100 transition"
              >
                Get Started
              </button>
              
              
            </div>
            
            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-white font-semibold mb-1">Real-time Updates</h3>
                <p className="text-gray-300 text-sm">Stay informed instantly</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-white font-semibold mb-1">Community Driven</h3>
                <p className="text-gray-300 text-sm">Connect with people</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-white font-semibold mb-1">Verified Events</h3>
                <p className="text-gray-300 text-sm">Quality assured</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostSubscriptionMobile;